<template>
  <div class="d-reviewer-directory">
    <section
      ref="reviewersSection"
      class="section-grey section-thin"
    >
      <div class="wrapper">
        <div class="reviewers-container">
          <div
            class="reviewers-list-container panel panel-overflow-md loadable"
            :class="{ 'loading': reviewerSearch.isSearching }"
          >
            <DuiLoadingIndicator
              v-if="reviewerSearch.isSearching"
              size="lg"
            />

            <div class="loaded-content">
              <div>
                <div v-if="(storeDisplayedSearchTags && storeDisplayedSearchTags.length)">
                  <DSearchTags store-search-prefix="ReviewerDirectory/ReviewerSearch" />
                  <DReviewerSearchNumberResults :total-count="reviewerSearch.meta.total_count" />
                </div>

                <div
                  v-if="!storeDisplayedSearchTags.length"
                  class="text-center space-bottom-3em"
                >
                  <p v-if="reviewerSearch.meta.total_count">
                    <strong>
                      {{ reviewerSearch.meta.total_count }} {{ reviewerSearch.meta.total_count | duiPluralize('reviewer', 'reviewers') }}
                    </strong>
                    waiting to be discovered...
                  </p>
                  <p v-else>
                    There are currently no more reviewers
                    <span class="emoji">🥺</span>
                  </p>
                </div>

                <div
                  v-if="!reviewerSearch.results.length"
                  class="no-results-with-vessels"
                >
                  <img
                    v-for="num in reviewerVesselNum"
                    :key="num"
                    :src="reviewerVesselPath"
                    alt="no-results"
                    title="No results"
                  />
                </div>

                <div
                  v-if="!!reviewerSearch.results.length && !reviewerSearch.isSearching"
                  class="reviewers-list"
                >
                  <DReviewerDirectoryEntry
                    v-for="(entry, index) in reviewerSearch.results"
                    :key="entry.slug"
                    :entry="entry"
                    :is-first-entry="index === 0"
                    :is-user-signed-in="isUserSignedIn"
                  />
                </div>

                <DuiPagination
                  v-if="(!reviewerSearch.isSearching && reviewerSearch.meta.total_pages > 1 && reviewerSearch.results.length)"
                  :total-pages="reviewerSearch.meta.total_pages"
                  :value="reviewerSearch.meta.current_page"
                  @input="setPageTag"
                />
              </div>
            </div>
          </div>

          <div class="reviewer-search">
            <DReviewerSearchForm store-search-prefix="ReviewerDirectory" />
            <DSubmitYourBook class="space-top-3em hidden-until-md" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Vue from 'vue';

import reviewerSearchService from '@d-services/reviewer-search-service/directory-search-service';
import DSubmitYourBook from './d-submit-your-book/d-submit-your-book.vue';
import DReviewerDirectoryEntry from './d-reviewer-directory-entry/d-reviewer-directory-entry.vue';
import DReviewerSearchForm from '@d-components/d-reviewer-search/d-reviewer-search-form/d-reviewer-search-form.vue';
import DReviewerSearchNumberResults from '@d-components/d-reviewer-search/d-reviewer-search-number-results/d-reviewer-search-number-results.vue';
import DSearchTags from '@d-components/d-search-tags/d-search-tags.vue';
import { getErrorMessage } from '@d-shared/utils/errors';
import { SCROLL_OPTIONS } from '@d-shared/config';

export default {
  components: {
    DSubmitYourBook,
    DReviewerDirectoryEntry,
    DReviewerSearchForm,
    DReviewerSearchNumberResults,
    DSearchTags,
  },
  props: {
    reviewerVesselPath: {
      type: String,
      default: '',
    },
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    reviewerSearch: {
      isSearching: true,
      meta: {},
      results: [],
    },
    reviewerVesselNum: 3,
  }),
  computed: {
    storeDisplayedSearchTags() {
      return this.$store.getters['ReviewerDirectory/ReviewerSearch/get-displayed-search-tags'];
    },
  },
  mounted() {
    this.loadReviewers();
  },
  methods: {
    async loadReviewers() {
      try {
        this.reviewerSearch = await reviewerSearchService.getReviewers();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }
    },

    setPageTag(pageNumber) {
      const tag = {
        hidden: true,
        key: 'page',
        page: true,
        text: `Page ${ pageNumber }`,
        value: (pageNumber !== 1) ? pageNumber : null,
      };

      this.$scrollTo(this.$refs.reviewersSection, SCROLL_OPTIONS);

      if (!tag.value) {
        this.$store.dispatch('ReviewerDirectory/ReviewerSearch/removeSearchTag', tag);
        return;
      }

      this.$store.dispatch('ReviewerDirectory/ReviewerSearch/addSearchTagAndSearch', tag);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-reviewer-directory {
  .reviewers-container {
    display: grid;
    grid-row-gap: $_3em;
    grid-template-areas:
      "reviewer-search"
      "reviewers-list-container";

    .reviewer-search {
      grid-area: reviewer-search;
      min-width: 0;
    }

    .reviewers-list-container {
      grid-area: reviewers-list-container;
    }

    @include screenMoreThan(sm) {
      grid-column-gap: $_6em;
      grid-row-gap: 0;
      grid-template-columns: repeat(3, 1fr);
      grid-template-areas:
        "reviewers-list-container reviewers-list-container reviewer-search";
    }

    .dui-pagination {
      margin: $_2em;
      padding-top: $_2em;
    }
  }

  .d-reviewer-search-form {
    @include screenMoreThan(sm) {
      margin-right: -$_3em;
    }
  }
}
</style>
